/**
 * Apply Now bridge.
 *  - Logged-in student hits /apply/{programme_id}.
 *  - We POST /api/enrollments (idempotent — returns existing if already enrolled).
 *  - We forward to /pay/{programme_id} when there's an unpaid balance, or to
 *    /programmes/{programme_id} when the student is already fully paid.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Warning } from "@phosphor-icons/react";

export default function ApplyProgrammePage() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("Enrolling you in this programme…");

  useEffect(() => {
    if (loading || !user) return;
    let cancelled = false;
    (async () => {
      try {
        setStatus("Enrolling you in this programme…");
        const { data } = await api.post("/enrollments", { programme_id: id });
        if (cancelled) return;
        const enrollmentId = data?.id;
        const balance = Number(data?.balance ?? 0);
        const enrStatus = data?.status;
        const paymentStatus = data?.payment_status;
        const appStatus = data?.application_status || "not_submitted";
        // Step 1 — pay if there's still a balance / status is pending_payment.
        if (balance > 0 || enrStatus === "pending_payment") {
          setStatus("Redirecting to payment…");
          navigate(`/pay/${id}`, { replace: true });
          return;
        }
        // Step 2 — admin still verifying payment.
        if (enrStatus === "pending_verification" || paymentStatus === "notified") {
          setStatus("Your payment is being verified — redirecting…");
          navigate("/payments", { replace: true });
          return;
        }
        // Step 3 — payment verified, application form due.
        if (["paid", "completed", "partial"].includes(paymentStatus) && appStatus !== "approved" && enrollmentId) {
          setStatus("Payment verified — please fill the application form.");
          navigate(`/application/${enrollmentId}`, { replace: true });
          return;
        }
        // Step 4 — everything done, programme view.
        setStatus("Opening your programme.");
        navigate(`/programmes/${id}`, { replace: true });
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || e.message || "Could not start your application.");
      }
    })();
    return () => { cancelled = true; };
  }, [id, user, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (!user) return <Navigate to={`/register?mode=programme&programme=${id}`} replace />;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="apply-programme-page">
      <Card className="max-w-md w-full border border-slate-200 rounded-none">
        <CardContent className="p-8 text-center">
          {error ? (
            <>
              <Warning size={32} weight="fill" className="text-amber-500 mx-auto mb-3" />
              <h1 className="text-lg font-bold text-slate-950 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Application paused</h1>
              <p className="text-sm text-slate-600 mb-4" data-testid="apply-error">{error}</p>
              <div className="flex gap-2 justify-center">
                <Link to={`/programmes/${id}`}><Button variant="outline" className="rounded-none">Open Programme</Button></Link>
                <Link to="/dashboard"><Button className="bg-blue-800 hover:bg-blue-900 text-white rounded-none">Go to Dashboard</Button></Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-slate-700">{status}</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
